import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useSearchParams,
  useNavigate,
  Link,
  useLocation,
} from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { IoMdClose } from "react-icons/io";
import { FaFilter, FaHeart } from "react-icons/fa";
import { toast } from "react-toastify";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ProductGridSkeleton } from "../components/ProductSkeleton";
// import NewsTicker from "../components/Home/Newtricker";
// import { NewBanner } from "../components/Home/Banner";
import Pagination from "../utilis/Pagination";
import { Buttons } from "../components/Buttons";
import CartDrawer from "../components/CartDrawer";
import banner from "../assets/baner.webp";
import { getProducts } from "../redux/api/products";
import { addToCart } from "../redux/store/cartSlice";
import { categoryData } from "../data/links";
import { server } from "../utilis/constant";
import { addToWishlistAPI } from "../redux/store/wishlist";
import WishlistButton from "../components/WishlistButton";
import { useAuth } from "../contexts/AuthContext";
// import banner from "../assets/fragrances.png";
// import "./style.css";

const Perfume = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const productsPerPage = 12;
  const { products, total, totalPages, page, loading } = useSelector(
    (state) => state.products,
  );
  // URL params
  // ---------------- URL PARAMS ----------------
  const category = searchParams.get("category") || "";
  const vendor = searchParams.get("vendor") || "";
  const searchParam = searchParams.get("search") || "";
  const pageParam = Number(searchParams.get("page")) || 1;

  const tags = useMemo(() => {
    const param = searchParams.get("tags");
    return param ? param.split(",").map((t) => t.trim()) : [];
  }, [searchParams.get("tags")]);

  // ---------------- STATE ----------------
  const [filters, setFilters] = useState({
    title: "",
    body_html: "",
    search: searchParam,
    tags,
    vendor,
  });

  const [currentPage, setCurrentPage] = useState(pageParam);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [cartDrawer, setCartDrawer] = useState(false);
  const { isLoggedIn } = useSelector((state) => state.user);
  const wishlistItems = useSelector((state) => state.wishlist.wishlistItems);
  const { openLoginModal } = useAuth();
  // ---------------- SYNC URL → STATE ----------------
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      tags,
      vendor,
      search: searchParam,
    }));

    setCurrentPage(pageParam);
  }, [tags, vendor, pageParam, searchParam]);

  // ---------------- API PARAMS ----------------
  const apiParams = useMemo(() => {
    return {
      page: currentPage,
      limit: productsPerPage,
      ...(category && { category }),
      ...(filters.vendor && { vendor: filters.vendor }),
      ...(filters.title && { title: filters.title }),
      ...(filters.body_html && { body_html: filters.body_html }),
      ...(filters.search && { search: filters.search }),
      ...(filters.tags.length > 0 && { tags: filters.tags.join(",") }),
    };
  }, [
    currentPage,
    category,
    filters.vendor,
    filters.title,
    filters.body_html,
    filters.search,
    filters.tags,
  ]);

  // ---------------- FETCH (DEBOUNCED) ----------------
  useEffect(() => {
    const delay = setTimeout(() => {
      dispatch(getProducts(apiParams));
    }, 300);

    return () => clearTimeout(delay);
  }, [dispatch, apiParams]);

  // ---------------- URL UPDATE HELPER ----------------
  const updateURL = useCallback(
    (updatedFilters, page = 1) => {
      const params = new URLSearchParams();

      if (category) params.set("category", category);
      if (updatedFilters.vendor) params.set("vendor", updatedFilters.vendor);
      if (updatedFilters.search) params.set("search", updatedFilters.search);
      if (updatedFilters.tags.length) {
        params.set("tags", updatedFilters.tags.join(","));
      }

      params.set("page", page);
      setSearchParams(params);
    },
    [category, setSearchParams],
  );

  // ---------------- FILTER CHANGE ----------------
  const handleFilterChange = useCallback(
    (field, value) => {
      setFilters((prev) => {
        const updated = { ...prev, [field]: value };
        updateURL(updated, 1);
        return updated;
      });

      setCurrentPage(1);
    },
    [updateURL],
  );
  const handleVendorChange = useCallback(
    (value) => {
      handleFilterChange("vendor", value);
    },
    [handleFilterChange],
  );
  // ---------------- RESET ----------------
  const handleResetFilters = useCallback(() => {
    const reset = {
      title: "",
      body_html: "",
      search: "",
      tags: [],
      vendor: "",
    };

    setFilters(reset);
    setCurrentPage(1);
    
    // Keep category but remove other params
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    params.set("page", "1");
    setSearchParams(params);
  }, [category, setSearchParams]);

  // ---------------- CART ----------------
  const handleAddToCart = useCallback(
    (product) => {
      dispatch(addToCart(product));
      toast.success("Added to Cart Successfully");
      setCartDrawerOpen(true);
    },
    [dispatch],
  );

  // ---------------- NAVIGATION ----------------
  const handleProduct = useCallback(
    (id) => {
      navigate(`/product_description/${id}`);
    },
    [navigate],
  );

  // ---------------- SCROLL ----------------
  useEffect(() => {
    requestAnimationFrame(() => {
      const topValue = window.innerWidth < 500 ? 0 : 750;
      window.scrollTo({ top: topValue, behavior: "smooth" });
    });
  }, [currentPage, vendor, category]);

  // ---------------- CATEGORY ----------------
  const selectedCategory = useMemo(() => {
    return (
      categoryData.find(
        (item) =>
          item?.tag?.toLowerCase() === category?.toLowerCase() ||
          item?.tag?.toLowerCase() === vendor?.toLowerCase(),
      ) || null
    );
  }, [category, vendor]);

  const handleWishlist = async (product) => {
    if (!isLoggedIn) {
      openLoginModal();
      return;
    }

    try {
      await dispatch(addToWishlistAPI(product._id)).unwrap();
      toast.success("Added to wishlist");
    } catch (error) {
      console.error('Wishlist error:', error);
      if (error.includes('Access denied') || error.includes('authentication token')) {
        // Token might be invalid, show login modal
        openLoginModal();
      } else if (error.includes('already in wishlist')) {
        toast.error("Already in wishlist");
      } else {
        toast.error(error || "Failed to add to wishlist");
      }
    }
  };

  const handleAddtoCart = (product) => {
    dispatch(addToCart(product));
    toast.success("Add to Cart Successfully");
    setCartDrawer(true);
  };

  return (
    <>
      <Helmet>
        <title>
          {selectedCategory
            ? selectedCategory.helment
            : "Gift of God Fragrances - Perfumes"}
        </title>
        <meta
          name="body_html"
          content={`Welcome to ${
            selectedCategory
              ? selectedCategory.helment
              : "Gift of God Fragrances - Perfumes"
          } page`}
        />
      </Helmet>

      {/* NAVBAR */}
      <div className="bg-black">
        <Navbar />
      </div>

      {/* BREADCRUMB */}
      <div className="bg-white w-full py-[1rem] px-[1rem] sm:px-[5rem] text-[16px] text-gray-500">
        <span className="text-gray-800">
          <Link to={"/"}>Home</Link>
        </span>{" "}
        {">"} {selectedCategory?.title} {selectedCategory?.subtitle}
      </div>
      <div
        className="relative h-[60vh] w-full flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `url(${banner})`, // replace with your image
        }}
      >
        {/* DARK + SMOKE OVERLAY */}
        <div className="absolute inset-0 bg-black/60"></div>

        {/* OPTIONAL SMOKE EFFECT LAYER */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/20 to-black/20"></div>

        {/* CONTENT */}
        <div className="relative z-10 text-center px-4">
          <h1 className="text-white text-4xl sm:text-5xl md:text-6xl font-bold tracking-[0.3em] uppercase">
            Fragrances
          </h1>

          <p className="mt-3 text-gray-300 text-sm sm:text-base tracking-widest">
            Discover Your Signature Scent
          </p>

          {/* decorative line */}
          <div className="mt-4 flex items-center justify-center gap-2">
            <span className="w-10 h-[1px] bg-white/60"></span>
            <span className="w-2 h-2 rounded-full bg-white/80"></span>
            <span className="w-10 h-[1px] bg-white/60"></span>
          </div>
        </div>
      </div>
      {/* PRODUCT INFO BAR */}
      <div className="py-8 px-4 sm:px-8 lg:px-12 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          {/* Search Results Header */}
          {filters.search && (
            <div className="mb-6 p-6 bg-gradient-to-r from-[#f0dc82]/10 to-[#b38f36]/10 border border-[#b38f36]/20 rounded-2xl shadow-sm">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-800 mb-2">
                    🔍 Search Results for: <span className="text-[#b38f36]">"</span>
                    <span className="text-[#b38f36] font-black">{filters.search}</span>
                    <span className="text-[#b38f36]">"</span>
                  </h2>
                  <p className="text-sm text-gray-600">
                    {products && products.length > 0 
                      ? `Found ${total} ${total === 1 ? 'product' : 'products'} matching your search`
                      : 'No products found for your search'
                    }
                  </p>
                </div>
                <button
                  onClick={() => {
                    setFilters(prev => ({ ...prev, search: "" }));
                    const params = new URLSearchParams(searchParams);
                    params.delete("search");
                    setSearchParams(params);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-[#b38f36] text-[#b38f36] rounded-full hover:bg-[#b38f36] hover:text-white transition-all duration-200 font-medium text-sm shadow-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear Search
                </button>
              </div>
            </div>
          )}
          
          {/* Product Count & Info */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-4">
              {products && products.length > 0 ? (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-[#b38f36] rounded-full"></div>
                  <span className="text-gray-700 font-medium">
                    Showing{" "}
                    <span className="font-bold text-[#b38f36]">
                      {(currentPage - 1) * productsPerPage + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-bold text-[#b38f36]">
                      {(currentPage - 1) * productsPerPage + products.length}
                    </span>{" "}
                    of{" "}
                    <span className="font-bold text-[#b38f36]">{total}</span>{" "}
                    {total === 1 ? 'product' : 'products'}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                  <span className="text-gray-500 font-medium">No products found</span>
                </div>
              )}
            </div>

            {/* Help Link */}
            <Link
              to={"/contact"}
              className="group flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#b38f36] to-[#8b6b2a] text-white rounded-full hover:from-[#8b6b2a] hover:to-[#6b5220] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-medium"
            >
              <svg className="w-5 h-5 group-hover:rotate-12 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Didn't find your perfume? Tell us!
            </Link>
          </div>
        </div>
      </div>

      {/* FILTER BUTTON (MOBILE ONLY) */}
      <div className="py-6 px-4 md:px-8 bg-white">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="md:hidden">
            <button
              onClick={() => setIsDrawerOpen(true)}
              className="flex items-center gap-3 bg-gradient-to-r from-[#b38f36] to-[#8b6b2a] text-white px-6 py-3 rounded-full font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
              </svg>
              Filters & Search
            </button>
          </div>
        </div>
      </div>

      {/* MAIN LAYOUT */}
      <div className="px-[0.5rem] md:px-[4rem] bg-white">
        <div className="flex flex-col md:flex-row gap-6 items-start">
          {/* ================= DESKTOP FILTER (REDESIGNED ONLY) ================= */}
          <div className="hidden md:block w-1/5">
            <div className="sticky top-20 bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl border border-gray-100 p-6 flex flex-col gap-6">
              {/* Header */}
              <div className="text-center pb-4 border-b border-gray-200">
                <div className="w-12 h-12 bg-gradient-to-br from-[#b38f36] to-[#8b6b2a] rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-800">Refine Search</h2>
                <p className="text-sm text-gray-500 mt-1">Find your perfect fragrance</p>
              </div>

              {/* Search */}
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-4 h-4 text-[#b38f36]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Search Filters</h3>
                </div>

                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Search by name..."
                    value={filters.title}
                    onChange={(e) => handleFilterChange("title", e.target.value)}
                    className="w-full p-3 rounded-xl bg-white border-2 border-gray-200 focus:border-[#b38f36] focus:ring-2 focus:ring-[#b38f36]/20 outline-none transition-all duration-200 text-sm placeholder-gray-400"
                  />

                  <input
                    type="text"
                    placeholder="Search description..."
                    value={filters.body_html}
                    onChange={(e) =>
                      handleFilterChange("body_html", e.target.value)
                    }
                    className="w-full p-3 rounded-xl bg-white border-2 border-gray-200 focus:border-[#b38f36] focus:ring-2 focus:ring-[#b38f36]/20 outline-none transition-all duration-200 text-sm placeholder-gray-400"
                  />
                </div>
              </div>

              {/* Tags */}
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-4 h-4 text-[#b38f36]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Categories</h3>
                </div>

                <div className="flex flex-wrap gap-2">
                  {[
                    ...(category !== "men" &&
                    category !== "women" &&
                    category !== "unisex"
                      ? ["Men", "Women"]
                      : []),
                    "New Arrivals",
                    "Our Specials",
                    "Top Sellers",
                    "Best Sales",
                  ].map((tag) => (
                    <label
                      key={tag}
                      className={`cursor-pointer px-4 py-2 rounded-full text-xs font-medium border-2 transition-all duration-200 hover:scale-105 ${
                        filters.tags.includes(tag)
                          ? "bg-gradient-to-r from-[#b38f36] to-[#8b6b2a] text-white border-[#b38f36] shadow-lg"
                          : "bg-white text-gray-600 border-gray-200 hover:border-[#b38f36] hover:text-[#b38f36]"
                      }`}
                    >
                      <input
                        type="checkbox"
                        value={tag}
                        checked={filters.tags.includes(tag)}
                        onChange={(e) => {
                          const newTags = e.target.checked
                            ? [...filters.tags, tag]
                            : filters.tags.filter((t) => t !== tag);

                          handleFilterChange("tags", newTags);
                        }}
                        className="hidden"
                      />
                      {tag}
                    </label>
                  ))}
                </div>
              </div>

              {/* Vendor */}
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-4 h-4 text-[#b38f36]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Brand</h3>
                </div>

                <select
                  value={filters.vendor}
                  onChange={(e) => handleVendorChange(e.target.value)}
                  className="w-full p-3 rounded-xl bg-white border-2 border-gray-200 focus:border-[#b38f36] focus:ring-2 focus:ring-[#b38f36]/20 outline-none transition-all duration-200 text-sm text-gray-700"
                >
                  <option value="">All Brands</option>
                  <option value="Gift of God Fragrances">Gift of God Fragrances</option>
                  <option value="Adrienne Vittadini">Adrienne Vittadini</option>
                  <option value="Versace">Versace</option>
                  <option value="Christian Dior">Christian Dior</option>
                  <option value="Clinique">Clinique</option>
                  <option value="Cartier">Cartier</option>
                  <option value="Carolina Herrera">Carolina Herrera</option>
                  <option value="Calvin Klein">Calvin Klein</option>
                  <option value="Bvlgari">Bvlgari</option>
                  <option value="Burberry">Burberry</option>
                  <option value="Azzaro">Azzaro</option>
                  <option value="Armaf">Armaf</option>
                </select>
              </div>

              {/* Reset */}
              <button
                type="button"
                onClick={handleResetFilters}
                className="w-full py-3 rounded-xl bg-black text-white font-semibold hover:from-gray-700 hover:to-gray-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reset All Filters
              </button>
            </div>
          </div>

          {/* ================= PRODUCTS ================= */}
          <div className="w-full md:w-4/5 flex flex-wrap justify-start items-start">
            {loading ? (
              // Show skeleton loading when products are loading
              <ProductGridSkeleton count={productsPerPage} />
            ) : products && products.length > 0 ? (
              // Show actual products when loaded
              products.map((product) => (
                <div
                  className="relative w-[240px] md:w-[300px] py-[1rem] px-1 md:px-4"
                  key={product.id}
                >
                  <div className="product-card overflow-hidden group h-full bg-white rounded-3xl border border-gray-200 hover:shadow-2xl hover:-translate-y-3 transition-all duration-500 flex flex-col relative">
                    {/* IMAGE */}
                    <div className="relative aspect-[4/3.2] bg-[#F8F4ED]">
                      <img
                        onClick={() =>
                          navigate(`/product_description/${product._id}`)
                        }
                        src={`${server}/${product.image || product.variants?.[0]?.image}`}
                        alt={product.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 cursor-pointer"
                      />

                      {/* NEW BADGE */}
                      {product.tags.includes("New Arrivals") && (
                        <div className="absolute top-5 left-5 bg-gradient-to-br from-[#D4AF37] to-[#B8972E] text-black text-xs font-bold px-5 py-1.5 rounded-full shadow-md">
                          NEW
                        </div>
                      )}

                      {/* WISHLIST */}
                      <div className="absolute top-5 right-5">
                        <WishlistButton product={product} size="sm" />
                      </div>
                    </div>

                    {/* CONTENT */}
                    <div className="px-6 pt-5 pb-6 flex flex-col flex-1 text-center">
                      <h3 className="font-semibold text-lg text-black line-clamp-2 mb-3 min-h-[52px]">
                        {product.title}
                      </h3>

                      <div className="flex justify-center text-[#D4AF37] mb-4">
                        {"★".repeat(Math.floor(product.rating || 4))}
                      </div>

                      <div className="mt-auto flex items-center justify-between">
                        <p className="text-3xl font-bold text-black">
                          ${product.variants[0].price}
                        </p>

                        <button
                          onClick={() => handleAddtoCart(product)}
                          className="px-5 py-2 bg-black text-white rounded-2xl opacity-0 group-hover:opacity-100 transition-all hover:bg-[#D4AF37] hover:text-black"
                        >
                          Add to Cart
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              // Show no products message when not loading and no products found
              <div className="w-full text-center py-16">
                <div className="max-w-md mx-auto">
                  <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">No Products Found</h3>
                  <p className="text-gray-600 mb-6">We couldn't find any products matching your criteria. Try adjusting your filters or search terms.</p>
                  <button
                    onClick={handleResetFilters}
                    className="px-6 py-3 bg-gradient-to-r from-[#b38f36] to-[#8b6b2a] text-white rounded-full hover:from-[#8b6b2a] hover:to-[#6b5220] transition-all duration-200 font-medium"
                  >
                    Clear All Filters
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* PAGINATION */}
        <div className="mt-8 flex justify-center">
          <Pagination
            itemsPerPage={productsPerPage}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </div>

      {/* MOBILE DRAWER */}
      {isDrawerOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            onClick={() => setIsDrawerOpen(false)}
          />
          <div
            className="fixed right-0 top-0 w-4/5 max-w-sm bg-white h-full z-50 shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-[#b38f36] to-[#8b6b2a] p-6 text-white">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold">Filters</h2>
                  <p className="text-sm opacity-90">Refine your search</p>
                </div>
                <button
                  onClick={() => setIsDrawerOpen(false)}
                  className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                >
                  <IoMdClose className="text-xl" />
                </button>
              </div>
            </div>

            {/* Mobile Filter Content */}
            <div className="p-6">
              <form
                className="flex flex-col gap-6"
                onSubmit={(e) => e.preventDefault()}
              >
                {/* Search Inputs */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-3">
                    <svg className="w-4 h-4 text-[#b38f36]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <h3 className="font-bold text-gray-800 uppercase tracking-wide text-sm">Search</h3>
                  </div>
                  
                  <input
                    type="text"
                    placeholder="Search by name..."
                    value={filters.title}
                    onChange={(e) => handleFilterChange("title", e.target.value)}
                    className="w-full p-4 rounded-xl bg-gray-50 border-2 border-gray-200 focus:border-[#b38f36] focus:bg-white outline-none transition-all text-sm"
                  />
                  
                  <input
                    type="text"
                    placeholder="Search description..."
                    value={filters.body_html}
                    onChange={(e) =>
                      handleFilterChange("body_html", e.target.value)
                    }
                    className="w-full p-4 rounded-xl bg-gray-50 border-2 border-gray-200 focus:border-[#b38f36] focus:bg-white outline-none transition-all text-sm"
                  />
                </div>

                {/* Tags */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <svg className="w-4 h-4 text-[#b38f36]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    <h3 className="font-bold text-gray-800 uppercase tracking-wide text-sm">Categories</h3>
                  </div>
                  
                  <div className="space-y-3">
                    {[
                      ...(category !== "men" &&
                      category !== "women" &&
                      category !== "unisex"
                        ? ["Men", "Women"]
                        : []),
                      "New Arrivals",
                      "Our Specials",
                      "Top Sellers",
                      "Best Sales",
                    ].map((tag) => (
                      <label
                        key={tag}
                        className="flex items-center gap-4 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          value={tag}
                          checked={filters.tags.includes(tag)}
                          onChange={(e) => {
                            const newTags = e.target.checked
                              ? [...filters.tags, tag]
                              : filters.tags.filter((t) => t !== tag);
                            handleFilterChange("tags", newTags);
                          }}
                          className="w-5 h-5 text-[#b38f36] bg-gray-100 border-2 border-gray-300 rounded focus:ring-[#b38f36] focus:ring-2"
                        />
                        <span className="text-gray-700 font-medium">{tag}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Vendor */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <svg className="w-4 h-4 text-[#b38f36]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <h3 className="font-bold text-gray-800 uppercase tracking-wide text-sm">Brand</h3>
                  </div>
                  
                  <select
                    value={filters.vendor}
                    onChange={(e) => handleVendorChange(e.target.value)}
                    className="w-full p-4 rounded-xl bg-gray-50 border-2 border-gray-200 focus:border-[#b38f36] focus:bg-white outline-none transition-all text-sm"
                  >
                    <option value="">All Brands</option>
                    <option value="Gift of God Fragrances">Gift of God Fragrances</option>
                    <option value="Adrienne Vittadini">Adrienne Vittadini</option>
                    <option value="Versace">Versace</option>
                    <option value="Christian Dior">Christian Dior</option>
                    <option value="Clinique">Clinique</option>
                    <option value="Cartier">Cartier</option>
                    <option value="Carolina Herrera">Carolina Herrera</option>
                    <option value="Calvin Klein">Calvin Klein</option>
                    <option value="Bvlgari">Bvlgari</option>
                    <option value="Burberry">Burberry</option>
                    <option value="Azzaro">Azzaro</option>
                    <option value="Armaf">Armaf</option>
                  </select>
                </div>

                {/* Reset Button */}
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="w-full py-4 rounded-xl bg-gradient-to-r from-gray-600 to-gray-700 text-white font-semibold hover:from-gray-700 hover:to-gray-800 transition-all duration-200 shadow-lg flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Reset All Filters
                </button>
              </form>
            </div>
          </div>
        </>
      )}

      {cartDrawerOpen && (
        <CartDrawer onClose={() => setCartDrawerOpen(false)} />
      )}

      <Footer />
    </>
  );
};

export default Perfume;
